import { apiFetch } from "../api.js";
import type {
  CreateNoteInput,
  Note,
  NoteRelatedEntityType,
  NotesListResponse,
  UpdateNoteInput,
} from "./types.js";

/**
 * Notes API module — Phase 4 Session 2.
 *
 * The compound SK (`<timestamp>#<note_id>`) is URL-encoded by these
 * helpers; callers pass back the `note_sk` field they received from
 * the list response. Avoids a `note_id` GSI by keeping point-lookups
 * to a single GetItem.
 */

export interface ListNotesByEntityArgs {
  entity_type: NoteRelatedEntityType;
  entity_id: string;
  include_archived?: boolean;
}

export function listNotesByEntity(
  args: ListNotesByEntityArgs,
): Promise<NotesListResponse> {
  const params = new URLSearchParams({
    entity_type: args.entity_type,
    entity_id: args.entity_id,
  });
  if (args.include_archived) params.set("include_archived", "true");
  return apiFetch<NotesListResponse>(`/v1/notes?${params.toString()}`);
}

export function listNotesByHousehold(
  includeArchived = false,
): Promise<NotesListResponse> {
  const qs = includeArchived ? "?include_archived=true" : "";
  return apiFetch<NotesListResponse>(`/v1/notes${qs}`);
}

export function createNote(input: CreateNoteInput): Promise<Note> {
  return apiFetch<Note>("/v1/notes", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getNote(noteSk: string): Promise<Note> {
  return apiFetch<Note>(`/v1/notes/${encodeURIComponent(noteSk)}`);
}

export function updateNote(
  noteSk: string,
  input: UpdateNoteInput,
): Promise<Note> {
  return apiFetch<Note>(`/v1/notes/${encodeURIComponent(noteSk)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteNote(noteSk: string): Promise<void> {
  return apiFetch<void>(`/v1/notes/${encodeURIComponent(noteSk)}`, {
    method: "DELETE",
  });
}
